"use client";
import React from "react";
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, spring, Img, staticFile, OffthreadVideo } from "remotion";
import { loadFont as loadDev } from "@remotion/google-fonts/NotoSansDevanagari";

// ─────────────────────────────────────────────────────────────────────────────
// YouTube "Hype" — PHONE mimic (STANDALONE PREVIEW, not wired into any doc).
// 2026-07-16 rebuild (Yash): was a desktop page with Hype as a chip in the action
// row. Now: a phone with the YT app open, and THREE beats — Like, Hype, Subscribe.
//
// The Hype mechanic changed and drives the whole comp:
//  • Hype is NOT a standalone action-row button anymore. It lives in the COMMENT BAR.
//  • The comment bar is a 2-page CAROUSEL (two dots): page 1 = top comment,
//    page 2 = Hype.
//  • Page 2 is LOCKED until the video is Liked. The like is what activates it —
//    dot 2 lights up, the bar rotates to the Hype page, and only then can it be tapped.
// So the order is causal, not decorative: Like → unlock → rotate → Hype → badge.
//
// Hype brand look = vivid pink→purple gradient + sparkle star (YT Oct-2025 redesign).
// ─────────────────────────────────────────────────────────────────────────────

// Palette now lives in the DARK/LIGHT themes below; PhoneMock pulls from whichever is
// selected. These two remain only because the (unused) ActionChip helper references them.
const CHIP_BG = "rgba(255,255,255,0.10)";
const TXT = "#f1f1f1";
const PINK = "#FF2E8B";
const PURPLE = "#8B5CFF";
const HYPE_GRAD = `linear-gradient(95deg, ${PINK} 0%, ${PURPLE} 100%)`;

// 🔥 CHANNEL LOGO COMES FROM R2, NOT staticFile(). Same law as the phone footage:
// Modal serves public/ from the BAKED HF bundle image and factory_4k_full pushes only
// src/ — a file added to public/ today is a 404 inside every container. staticFile()
// here killed 3 CTA clusters on Kanwar (4f64cf48) 2026-07-28; the image-load error
// surfaces as a Remotion CancelledError and takes the whole fan-out down with it.
const CHANNEL_LOGO =
  "https://pub-bb3ce5df1bc243da96f744a22c134897.r2.dev/assets/assets/deva_katha_green_logo.png";

// ── THEME (added 2026-07-28, Yash: "can we make the dark phone white phone screen?") ──
// The module consts above stay as the DARK values so nothing that already renders changes.
// PhoneMock re-declares the same names locally from the chosen theme, which shadows them
// inside the component — so every existing style picks up the theme with no JSX rewrite.
// Hype pink/purple is brand colour and is deliberately identical in both themes.
type Theme = {
  bg: string; txt: string; subtxt: string; chip: string; bar: string; dead: string; blue: string;
  ripple: string;        // tap ripple — white is invisible on a light screen
  track: string;         // player progress-bar track
  hairline: string;      // up-next divider
  subBg: string; subTxt: string; subbedBg: string;   // Subscribe pill, un-subbed → subbed
  barBorder: string;     // comment-bar outline — the box must READ, not just tint
  chipBorder: string;    // Hype chip outline while un-hyped
  hypeLockedBg: string;  // Hype chip while still locked
  avatar: string;        // comment avatar placeholder
};
const DARK: Theme = {
  bg: "#0f0f0f", txt: "#f1f1f1", subtxt: "#aaaaaa", chip: "rgba(255,255,255,0.10)",
  bar: "#2b2b2b", dead: "#6b6b6b", blue: "#3ea6ff", ripple: "#ffffff",
  track: "rgba(255,255,255,0.25)", hairline: "rgba(255,255,255,0.08)",
  subBg: "#f1f1f1", subTxt: "#0f0f0f", subbedBg: "rgba(255,255,255,0.10)",
  hypeLockedBg: "rgba(255,255,255,0.04)", avatar: "#4a3a63",
  barBorder: "rgba(255,255,255,0.34)", chipBorder: "rgba(255,255,255,0.45)",
};
// YouTube light mode: white page, near-black type, grey secondaries, BLACK subscribe pill
// with white label (the inverse of dark mode), and #065fd4 as the accent instead of #3ea6ff
// — the pale blue is unreadable on white.
const LIGHT: Theme = {
  bg: "#ffffff", txt: "#0f0f0f", subtxt: "#5a5a5a", chip: "rgba(0,0,0,0.16)",
  bar: "#d9d9d9", dead: "#7d7d7d", blue: "#065fd4", ripple: "#0f0f0f",
  track: "rgba(0,0,0,0.15)", hairline: "rgba(0,0,0,0.10)",
  subBg: "#0f0f0f", subTxt: "#ffffff", subbedBg: "rgba(0,0,0,0.05)",
  hypeLockedBg: "rgba(0,0,0,0.13)", avatar: "#a892c2",
  barBorder: "rgba(0,0,0,0.42)", chipBorder: "rgba(0,0,0,0.52)",
};

// The title + top comment are Hindi — without a real Devanagari face they render as
// tofu boxes (there is NO system Devanagari on this box). Same loader the other
// Hindi comps use; keep Roboto ahead of it for the Latin/UI chrome.
const { fontFamily: DEV } = loadDev("normal", { weights: ["400", "600", "700"], subsets: ["devanagari", "latin"] });
const FONT = `Roboto, "Segoe UI", ${DEV}, system-ui, Arial, sans-serif`;
const FONT_HI = `${DEV}, Roboto, system-ui, Arial, sans-serif`;

// ── geometry, derived from the real app screenshot (youtube_app.jpg, 1080px wide) ──
// Every size below = (measured px in the screenshot / 1080) * 636, where 636 is our
// phone's SCREEN width (660 bezel − 2×12 padding). Do NOT eyeball these: the text was
// ~1.4× too small before because it was guessed rather than matched.
// 2026-07-16: phone shrunk 25% (660→495). The player FOLLOWS the narrower screen and
// stays 16:9 — never squash it. The F_* type sizes below are deliberately NOT scaled,
// so text keeps the same on-screen size and reads larger relative to the phone.
const PHONE_W = 495;
const PHONE_H = 930;
const BEZEL = 9;
const SCREEN_W = PHONE_W - BEZEL * 2;                // 477
const PLAYER_16x9 = Math.round(SCREEN_W / 16 * 9);   // 268 — the player is ALWAYS 16:9
const F_TITLE = 26;
const F_META = 19;
const F_SUB = 20;      // Subscribe pill label
const F_LABEL = 20;    // "Comments 1.2K"
const F_COMMENT = 20;
const F_HANDLE = 15;
const F_CHIP = 20;     // Give Hype / Hyped
const F_BADGE = 18;
const ICON = 33;       // like / dislike / share / download
const AVATAR = 42;
const PAGE_H = 54;     // carousel page height (was 44 — too tight for 20px text)

// ── beat sheet (seconds) ──
// Default = the standalone portrait preview's own pacing. The 16:9 overlay OVERRIDES
// every beat so the phone does what the narrator is SAYING, when he says it — that's
// what "match the audio" means here, not just A/V alignment.
// `share` is OPTIONAL — added 2026-07-28 for Kanwar Yatra, whose outro says "शेयर करें" out loud.
// Omit it and the share icon stays inert exactly as before (Kedarnath demo is unaffected).
export type Beats = {
  like: number; unlock: number; rotate: number; hype: number; badge: number; sub: number;
  share?: number;
};
const DEFAULT_BEATS: Beats = {
  like: 1.1,
  unlock: 1.6,   // dot 2 goes dead → lit (caused by the like)
  rotate: 1.9,   // carousel turns to the Hype page
  hype: 3.1,
  badge: 4.3,
  sub: 5.4,
};

const StarIcon: React.FC<{ size: number; fill: string; stroke?: string; strokeW?: number }> = ({
  size, fill, stroke, strokeW = 0,
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: "block" }}>
    <path
      d="M12 2.2l2.95 6.36 6.8 1.02-4.9 4.77 1.16 6.85L12 17.9 5.99 21.2l1.16-6.85-4.9-4.77 6.8-1.02L12 2.2z"
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeW}
      strokeLinejoin="round"
    />
  </svg>
);

const ThumbIcon: React.FC<{ size: number; color: string; filled?: boolean }> = ({ size, color, filled }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M3 10h3v10H3V10zm5 0l4.2-7.4c.7-1.2 2.6-.7 2.6.7V8h5.1c1.3 0 2.2 1.2 1.9 2.4l-1.7 7C21.8 18.5 21 19 20 19H8V10z"
      stroke={color} strokeWidth={1.7} strokeLinejoin="round" fill={filled ? color : "none"} />
  </svg>
);

const ShareIcon: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M13 4l7 6-7 6v-3.5C7.5 12.5 5 15 4 19c-.3-6 3-10 9-10.6V4z"
      stroke={color} strokeWidth={1.7} strokeLinejoin="round" />
  </svg>
);

const BellIcon: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M12 3a5 5 0 00-5 5v3.5L5.5 15h13L17 11.5V8a5 5 0 00-5-5zM10 18a2 2 0 004 0"
      stroke={color} strokeWidth={1.7} strokeLinejoin="round" strokeLinecap="round" />
  </svg>
);

// One radial sparkle emitted from the star on tap.
const Sparkle: React.FC<{ angle: number; dist: number; t: number; delay: number; color: string; sz: number }> = ({
  angle, dist, t, delay, color, sz,
}) => {
  const p = Math.min(Math.max((t - delay) / 0.55, 0), 1);
  if (p <= 0 || p >= 1) return null;
  const ease = 1 - Math.pow(1 - p, 2);
  const x = Math.cos(angle) * dist * ease;
  const y = Math.sin(angle) * dist * ease;
  const op = interpolate(p, [0, 0.2, 1], [0, 1, 0]);
  const scale = interpolate(p, [0, 0.3, 1], [0.2, 1, 0.3]);
  return (
    <div style={{
      position: "absolute", left: "50%", top: "50%",
      transform: `translate(-50%,-50%) translate(${x}px,${y}px) scale(${scale}) rotate(${angle}rad)`,
      opacity: op,
    }}>
      <StarIcon size={sz} fill={color} />
    </div>
  );
};

// Expanding white ripple under a finger tap. Rendered inside the tapped element.
// NOTE: must bail BEFORE the tap — `extrapolateLeft:"clamp"` holds opacity at the
// start value (0.5) for every t < at, which painted a white blob on Like/Subscribe
// from frame 0. The time gate is the fix, not the interpolate.
const Ripple: React.FC<{ t: number; at: number; x: number; y: number; color?: string }> = ({
  t, at, x, y, color = "#ffffff",
}) => {
  if (t < at) return null;
  const grow = interpolate(t, [at, at + 0.4], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const op = interpolate(t, [at, at + 0.4], [0.5, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (op <= 0.001) return null;
  return (
    <div style={{
      position: "absolute", left: x, top: y, width: 22, height: 22, borderRadius: 22,
      background: color, transform: `translate(-50%,-50%) scale(${1 + grow * 5})`,
      opacity: op, pointerEvents: "none",
    }} />
  );
};

// ── BIG ACTION FLASH over the video pane (Yash, 2026-07-28; redesigned same day) ──
// v1 was a bare word popped over the footage — Yash: "I didn't like your presentation,
// it should do better than that on the text part." Rebuilt as a proper lockup:
//
//   • SCRIM        — a dark vignette fades in under it, so the word reads over ANY frame
//                    instead of fighting whatever the footage happens to be doing.
//   • ICON         — the action's own glyph (bell / share / thumb / star), spring-popped.
//   • WORD         — always WHITE, heavy, wide-tracked. Colour is carried by the icon and
//                    the rule, not the type: white type is legible on every frame, and it
//                    keeps the four confirmations reading as ONE system.
//   • ACCENT RULE  — wipes out from the centre in the action's colour, under the word.
//
// Motion is staged rather than one pop: scrim → icon → word rises → rule wipes.
const FLASH_IN = 0.18, FLASH_HOLD = 0.95, FLASH_OUT = 1.35;
type FlashKind = "subscribe" | "share" | "like" | "hype";
// Yash: "have it in boxes — like we have the lower HYPE — and add some fireworks for each,
// and do not use the same hype colours for all, use different for different."
// So: same BADGE shape as the leaderboard chip, but each action owns its palette, and each
// gets its own sparkle burst in its own colours. Only HYPE keeps the pink→purple brand grad.
const FLASH_STYLE: Record<FlashKind, { grad: string; glow: string; spark: [string, string] }> = {
  subscribe: { grad: "linear-gradient(95deg, #FF4B4B 0%, #C50000 100%)", glow: "rgba(255,60,60,0.55)",  spark: ["#FF7A7A", "#FFD166"] },
  share:     { grad: "linear-gradient(95deg, #2BD9A8 0%, #0B8F68 100%)", glow: "rgba(43,217,168,0.55)", spark: ["#7DF3CE", "#CFFBEC"] },
  like:      { grad: "linear-gradient(95deg, #58B4FF 0%, #0B5FD4 100%)", glow: "rgba(88,180,255,0.55)", spark: ["#9BD5FF", "#E1F1FF"] },
  hype:      { grad: HYPE_GRAD,                                          glow: "rgba(255,46,139,0.55)", spark: [PINK, PURPLE] },
};
// `extraHold` stretches ONLY the hold+fade of one badge (Yash: "keep HYPED a little longer").
// It cannot shift any beat, so the 1.885s rhythm is unaffected.
const ActionFlash: React.FC<{ t: number; at: number; label: string; kind: FlashKind; fps: number; frame: number; extraHold?: number }> = ({
  t, at, label, kind, fps, frame, extraHold = 0,
}) => {
  const hold = FLASH_HOLD + extraHold, out = FLASH_OUT + extraHold;
  const p = t - at;
  if (p < -0.05 || p > out) return null;

  const pop = spring({ frame: frame - Math.round(at * fps), fps,
                       config: { damping: 11, stiffness: 190 }, durationInFrames: 26 });
  const scrimOp = interpolate(p, [0, FLASH_IN, hold, out], [0, 0.62, 0.62, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const bodyOp = interpolate(p, [0, FLASH_IN * 0.8, hold, out - 0.12], [0, 1, 1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const S = FLASH_STYLE[kind];
  const iconSz = 32;
  const glyph =
    kind === "subscribe" ? <BellIcon size={iconSz} color="#ffffff" />
    : kind === "share"   ? <ShareIcon size={iconSz} color="#ffffff" />
    : kind === "like"    ? <ThumbIcon size={iconSz} color="#ffffff" filled />
    : <StarIcon size={iconSz} fill="#ffffff" />;

  // fireworks — one burst per action, in THAT action's colours
  const sparks = Array.from({ length: 16 }).map((_, i) => {
    const angle = (i / 16) * Math.PI * 2 + 0.35;
    const dist = 96 + (i % 4) * 26;
    const sz = 11 + (i % 3) * 5;
    return <Sparkle key={i} angle={angle} dist={dist} t={t} delay={at + 0.04}
                    color={i % 2 === 0 ? S.spark[0] : S.spark[1]} sz={sz} />;
  });

  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {/* scrim — guarantees the badge reads over ANY frame of footage */}
      <div style={{
        position: "absolute", inset: 0, opacity: scrimOp,
        background: "radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.52) 55%, rgba(0,0,0,0.12) 100%)",
      }} />
      <div style={{
        position: "absolute", inset: 0, display: "flex",
        alignItems: "center", justifyContent: "center", opacity: bodyOp,
      }}>
        <div style={{ position: "relative" }}>
          {/* burst sits UNDER the badge, radiating from its centre */}
          <div style={{ position: "absolute", left: "50%", top: "50%", width: 0, height: 0 }}>{sparks}</div>
          {/* the BOX — same badge language as the Hype leaderboard chip */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 12,
            height: 64, padding: "0 26px", borderRadius: 32,   // = height/2 -> the Hype chip pill shape
            background: S.grad, whiteSpace: "nowrap",
            transform: `scale(${0.62 + pop * 0.38})`, transformOrigin: "center",
            boxShadow: `0 12px 34px rgba(0,0,0,0.55), 0 0 34px ${S.glow}`,
          }}>
            <div style={{ lineHeight: 0, transform: "translateY(4px)",
                          filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.45))" }}>{glyph}</div>
            <span style={{
              fontFamily: FONT, fontWeight: 900, fontSize: 36, letterSpacing: "0.085em",
              color: "#ffffff", lineHeight: 1.05, transform: "translateY(4px)",
              textShadow: "0 2px 8px rgba(0,0,0,0.45)",
            }}>{label}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ActionChip: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 7,
    height: 36, padding: "0 14px", borderRadius: 18,
    background: CHIP_BG, color: TXT, fontFamily: FONT, fontSize: 14, fontWeight: 500,
    whiteSpace: "nowrap", position: "relative", overflow: "hidden", ...style,
  }}>{children}</div>
);

// The phone itself. Used two ways:
//  • HypeButton      — standalone portrait preview (own backdrop, up-next rows).
//  • HypePhone16x9   — popped over real doc footage (no backdrop, no up-next rows,
//                      beats shifted by `offset` so the Subscribe tap lands exactly
//                      on the spoken word "सब्सक्राइब").
export const PhoneMock: React.FC<{
  beats?: Beats;          // absolute comp-seconds for each beat
  showUpNext?: boolean;   // other videos — hidden when overlaid on a real 16:9
  fadeOut?: boolean;      // tail fade (standalone loop only)
  screenSrc?: string;     // real footage to play INSIDE the phone's player
  title?: string;         // must match whatever screenSrc actually shows
  playerH?: number;       // inner player height. MUST stay 16:9 against the 636px screen
                          // (= 358). Shrinking this crops the video — make the whole
                          // phone smaller via scale instead.
  theme?: "dark" | "light";   // default dark = the original look, unchanged
  commentHandle?: string;     // top comment — MUST be production-specific, see below
  commentText?: string;
  flashLabels?: boolean;      // big LIKED/SHARED/HYPED/SUBSCRIBED over the video pane
  hypedExtraHold?: number;    // HYPED is the last action, so a longer tail costs nothing
}> = ({
  beats = DEFAULT_BEATS, showUpNext = true, fadeOut = true, screenSrc,
  title = "देवशयनी एकादशी का रहस्य · पंढरपुर के विट्ठल",
  playerH = PLAYER_16x9,
  theme = "dark",
  commentHandle = "@bhakti_ram · 2h",
  commentText = "जय विट्ठल! 🙏 ये कहानी सुनकर रोंगटे खड़े हो गए",
  flashLabels = false,
  hypedExtraHold = 1.2,
}) => {
  // Local re-declarations SHADOW the module-level dark consts for everything below,
  // so the whole component themes itself without touching a single style object.
  const TH = theme === "light" ? LIGHT : DARK;
  const YT_BG = TH.bg, CHIP_BG = TH.chip, BAR_BG = TH.bar;
  const TXT = TH.txt, SUBTXT = TH.subtxt, DEAD = TH.dead, BLUE = TH.blue;

  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const t = frame / fps;
  const total = durationInFrames / fps;
  const sec = (s: number) => Math.round(s * fps);
  const LIKE_TAP = beats.like;
  const UNLOCK = beats.unlock;
  const ROTATE = beats.rotate;
  const HYPE_TAP = beats.hype;
  const BURST = HYPE_TAP + 0.05;
  const BADGE = beats.badge;
  const SUB_TAP = beats.sub;
  const SHARE_TAP = beats.share;

  // ── LIKE ──
  const likePress = interpolate(t, [LIKE_TAP - 0.08, LIKE_TAP, LIKE_TAP + 0.12], [1, 0.92, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });
  const liked = t >= LIKE_TAP;
  const likePop = spring({ frame: frame - sec(LIKE_TAP), fps, config: { damping: 9, stiffness: 200 }, durationInFrames: 24 });
  const likeScale = liked ? 0.75 + likePop * 0.35 : 1;

  // ── SHARE (optional beat) ──
  // Same tap vocabulary as Like: press-squash + ripple, then the icon HOLDS blue so the
  // viewer can see which of the four icons was hit. Inert when beats.share is undefined.
  const sharePress = SHARE_TAP == null ? 1 : interpolate(
    t, [SHARE_TAP - 0.08, SHARE_TAP, SHARE_TAP + 0.12], [1, 0.92, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const shared = SHARE_TAP != null && t >= SHARE_TAP;
  const sharePop = spring({
    frame: frame - sec(SHARE_TAP ?? 0), fps,
    config: { damping: 9, stiffness: 200 }, durationInFrames: 24 });
  const shareScale = shared ? 0.75 + sharePop * 0.35 : 1;

  // ── HYPE UNLOCK (caused by the like) ──
  // dot 2 crossfades dead → lit; a glow pulses through the bar to sell the causality.
  const unlock = interpolate(t, [UNLOCK, UNLOCK + 0.3], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });
  const unlockGlow = interpolate(t, [UNLOCK, UNLOCK + 0.25, UNLOCK + 0.9], [0, 1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  // ── CAROUSEL rotate: page 1 (comment) → page 2 (hype) ──
  const rotSpr = spring({ frame: frame - sec(ROTATE), fps, config: { damping: 14, stiffness: 110 }, durationInFrames: 30 });
  const page = t >= ROTATE ? rotSpr : 0;      // 0 = comment page, 1 = hype page

  // ── HYPE tap ──
  const hypePress = interpolate(t, [HYPE_TAP - 0.08, HYPE_TAP, HYPE_TAP + 0.12], [1, 0.92, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });
  const fill = interpolate(t, [BURST, BURST + 0.35], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });
  const starPop = spring({ frame: frame - sec(BURST), fps, config: { damping: 9, stiffness: 170 }, durationInFrames: 30 });
  const starScale = fill > 0 ? 0.7 + starPop * 0.5 : 1;
  const labelSwap = interpolate(t, [BURST + 0.05, BURST + 0.25], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });
  const plusP = interpolate(t, [BURST, BURST + 0.9], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const plusOp = interpolate(plusP, [0, 0.15, 0.8, 1], [0, 1, 1, 0]);
  const plusY = interpolate(plusP, [0, 1], [0, -44]);

  // ── leaderboard badge + subscribe ──
  const badgeSpr = spring({ frame: frame - sec(BADGE), fps, config: { damping: 12, stiffness: 140 }, durationInFrames: 30 });
  const subPress = interpolate(t, [SUB_TAP - 0.08, SUB_TAP, SUB_TAP + 0.12], [1, 0.92, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });
  const subbed = t >= SUB_TAP;
  const subSwap = interpolate(t, [SUB_TAP, SUB_TAP + 0.2], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  const loopFade = fadeOut
    ? interpolate(t, [total - 0.4, total], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })
    : 1;

  const sparkles = Array.from({ length: 14 }).map((_, i) => {
    const angle = (i / 14) * Math.PI * 2 + 0.2;
    const dist = 40 + (i % 3) * 14;
    const sz = 9 + (i % 3) * 4;
    return <Sparkle key={i} angle={angle} dist={dist} t={t} delay={BURST} color={i % 2 === 0 ? PINK : PURPLE} sz={sz} />;
  });

  return (
    <div style={{ opacity: loopFade }}>
      {/* ── PHONE ── bezel + screen */}
      <div style={{
        width: PHONE_W, height: PHONE_H, borderRadius: 39, background: "#000",
        padding: BEZEL, boxShadow: `0 30px 90px rgba(0,0,0,0.7), 0 0 0 2px ${theme === "light" ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.10)"}`,
      }}>
        <div style={{
          width: "100%", height: "100%", borderRadius: 31, background: YT_BG,
          overflow: "hidden", position: "relative",
        }}>
          {/* status bar */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "12px 18px 6px", color: TXT, fontFamily: FONT, fontSize: F_META, fontWeight: 600,
          }}>
            <span>9:41</span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <div style={{ width: 16, height: 10, borderRadius: 2, background: TXT, opacity: 0.9 }} />
              <div style={{ width: 22, height: 11, borderRadius: 3, border: `1.5px solid ${TXT}`, opacity: 0.9 }} />
            </div>
          </div>

          {/* video player */}
          <div style={{
            margin: "4px 0 0", width: "100%", height: playerH,
            background: "linear-gradient(150deg, #3a1d55 0%, #171022 60%, #0d0b12 100%)",
            position: "relative", display: "flex", alignItems: "flex-end",
          }}>
            {/* The phone plays the REAL clip that's running when "सब्सक्राइब" is spoken
                (cut from the finished 720p). Falls back to a faint glow when no source
                is supplied, so the standalone preview still reads as a video. */}
            {screenSrc ? (
              <OffthreadVideo src={screenSrc} muted
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <div style={{
                position: "absolute", left: "50%", top: "48%", transform: "translate(-50%,-50%)",
                width: 190, height: 190, borderRadius: "50%",
                background: "radial-gradient(circle, rgba(255,190,90,0.30) 0%, rgba(255,120,40,0.10) 55%, transparent 72%)",
              }} />
            )}
            {/* channel watermark, top-right — the app shows it over the player */}
            <div style={{ position: "absolute", right: 14, top: 12, width: 34, height: 34, opacity: 0.85 }}>
              <Img src={CHANNEL_LOGO}
                   style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            </div>
            {/* big action confirmations, over the footage — one per beat as it fires */}
            {flashLabels && (
              <>
                <ActionFlash t={t} frame={frame} fps={fps} at={SUB_TAP} label="SUBSCRIBED" kind="subscribe" />
                {SHARE_TAP != null && <ActionFlash t={t} frame={frame} fps={fps} at={SHARE_TAP} label="SHARED" kind="share" />}
                <ActionFlash t={t} frame={frame} fps={fps} at={LIKE_TAP} label="LIKED" kind="like" />
                <ActionFlash t={t} frame={frame} fps={fps} at={HYPE_TAP} label="HYPED" kind="hype" extraHold={hypedExtraHold} />
              </>
            )}
            <div style={{ width: "100%", height: 3, background: TH.track }}>
              <div style={{ width: "38%", height: "100%", background: "#ff0000" }} />
            </div>
          </div>

          <div style={{ padding: "14px 16px 0" }}>
            {/* title */}
            <div style={{ color: TXT, fontFamily: FONT_HI, fontSize: F_TITLE, fontWeight: 700, lineHeight: 1.28 }}>
              {title}
            </div>
            <div style={{ color: SUBTXT, fontFamily: FONT, fontSize: F_META, marginTop: 8 }}>
              <span style={{ color: TXT, fontWeight: 600 }}>@DevaKathaOfficial</span> · 2.1M views · 9 hr ago
              <span style={{ color: SUBTXT }}> …more</span>
            </div>

            {/* ACTION ROW — matches the real app screenshot (youtube_app.jpg 2026-07-16):
                [green avatar] [Subscribe] then ICON-ONLY like/dislike/share/download/⋯
                (no counts, no separate channel row). NO Hype chip here — Hype lives in
                the comment bar now. */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16 }}>
              <div style={{
                width: AVATAR, height: AVATAR, borderRadius: AVATAR/2, flexShrink: 0, overflow: "hidden",
              }}>
                <Img src={CHANNEL_LOGO}
                     style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>

              <div style={{
                position: "relative", overflow: "hidden",
                transform: `scale(${subPress})`, transformOrigin: "center", flexShrink: 0,
                height: 42, padding: "0 14px", borderRadius: 21,
                background: subbed ? TH.subbedBg : TH.subBg,
                display: "flex", alignItems: "center", gap: 7,
              }}>
                {subbed && <BellIcon size={20} color={TXT} />}
                <span style={{
                  color: subbed ? TXT : TH.subTxt,
                  fontFamily: FONT, fontSize: F_SUB, fontWeight: 600,
                }}>{subbed ? "Subscribed" : "Subscribe"}</span>
                <Ripple t={t} at={SUB_TAP} x={50} y={19} color={theme === "light" ? "#ffffff" : TH.ripple} />
              </div>

              {/* icon-only actions, evenly spread like the app */}
              <div style={{ display: "flex", alignItems: "center", gap: 13, marginLeft: 2 }}>
                <div style={{ position: "relative", transform: `scale(${likePress})` }}>
                  <div style={{ transform: `scale(${likeScale})`, transformOrigin: "center" }}>
                    <ThumbIcon size={ICON} color={liked ? BLUE : TXT} filled={liked} />
                  </div>
                  <Ripple t={t} at={LIKE_TAP} x={12} y={12} color={TH.ripple} />
                </div>
                {/* dislike = thumb DOWN: scale(-1,-1) rotates 180°. scaleX(-1) alone only
                    mirrors it and still reads as a thumbs-up. */}
                <div style={{ transform: "scale(-1,-1)" }}><ThumbIcon size={ICON} color={TXT} /></div>
                <div style={{ position: "relative", transform: `scale(${sharePress})` }}>
                  <div style={{ transform: `scale(${shareScale})`, transformOrigin: "center" }}>
                    <ShareIcon size={ICON} color={shared ? BLUE : TXT} />
                  </div>
                  {SHARE_TAP != null && <Ripple t={t} at={SHARE_TAP} x={12} y={12} color={TH.ripple} />}
                </div>
                <svg width={ICON} height={ICON} viewBox="0 0 24 24" fill="none">
                  <path d="M12 3v13m0 0l-5-5m5 5l5-5M4 20h16" stroke={TXT} strokeWidth={1.7}
                        strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>

            {/* ── COMMENT BAR — the 2-page carousel. Page 2 (Hype) unlocks on the Like. ──
                This is the SUBJECT of the overlay, so it gets the breathing room the
                shrunken player gave up. */}
            <div style={{
              marginTop: 18, borderRadius: 14, background: BAR_BG, padding: "16px 16px",
              border: `3px solid ${TH.barBorder}`,
              position: "relative", overflow: "hidden",
              boxShadow: unlockGlow > 0.01 ? `0 0 0 2px rgba(255,46,139,${0.55 * unlockGlow}), 0 0 26px rgba(255,46,139,${0.4 * unlockGlow})` : "none",
            }}>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8,
              }}>
                <span style={{ color: TXT, fontFamily: FONT, fontSize: F_LABEL, fontWeight: 600 }}>
                  Comments <span style={{ color: SUBTXT, fontWeight: 400 }}>1.2K</span>
                </span>
                {/* the TWO DOTS — dot 2 is dead until the like unlocks Hype */}
                <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
                  <div style={{
                    width: 7, height: 7, borderRadius: 4,
                    background: page < 0.5 ? TXT : DEAD,
                  }} />
                  <div style={{
                    width: 7, height: 7, borderRadius: 4,
                    background: unlock < 0.5 ? DEAD : (page >= 0.5 ? PINK : "rgba(255,46,139,0.45)"),
                    boxShadow: unlock > 0.5 ? `0 0 ${8 * unlock}px ${PINK}` : "none",
                    transform: `scale(${1 + unlockGlow * 0.5})`,
                  }} />
                </div>
              </div>

              {/* pages viewport — rotates up by one page height */}
              <div style={{ height: PAGE_H, position: "relative", overflow: "hidden" }}>
                <div style={{ transform: `translateY(${-page * PAGE_H}px)` }}>
                  {/* PAGE 1 — top comment */}
                  <div style={{ height: PAGE_H, display: "flex", alignItems: "center", gap: 10, opacity: 1 - page * 0.85 }}>
                    <div style={{ width: 31, height: 31, borderRadius: 16, background: TH.avatar, flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: SUBTXT, fontFamily: FONT, fontSize: F_HANDLE }}>{commentHandle}</div>
                      <div style={{
                        color: TXT, fontFamily: FONT_HI, fontSize: F_COMMENT, whiteSpace: "nowrap",
                        overflow: "hidden", textOverflow: "ellipsis", maxWidth: 360,
                      }}>
                        {commentText}
                      </div>
                    </div>
                  </div>

                  {/* PAGE 2 — HYPE (locked → active). Chip sits RIGHT, hint on the left. */}
                  <div style={{
                    height: PAGE_H, display: "flex", alignItems: "center", justifyContent: "flex-end",
                    transform: `scale(${hypePress})`, transformOrigin: "right center",
                  }}>
                    {/* locked hint — only while Hype is still dead */}
                    <span style={{
                      marginRight: 12, color: DEAD, fontFamily: FONT, fontSize: 16,
                      opacity: Math.max(0, 1 - unlock),
                    }}>Like to unlock</span>
                    <div style={{ position: "relative" }}>
                      {/* sparkle burst layer */}
                      <div style={{ position: "absolute", left: 24, top: 18, width: 0, height: 0, pointerEvents: "none" }}>
                        {sparkles}
                      </div>
                      <div style={{
                        display: "flex", alignItems: "center", gap: 8,
                        height: 44, padding: "0 18px", borderRadius: 22,
                        // locked = flat dead grey; unlocked = live; hyped = gradient
                        background: fill > 0 ? HYPE_GRAD : (unlock > 0.5 ? CHIP_BG : TH.hypeLockedBg),
                        border: fill > 0 ? "3px solid rgba(255,255,255,0.55)" : `3px solid ${TH.chipBorder}`,
                        boxShadow: fill > 0 ? `0 6px 20px rgba(255,46,139,${0.45 * fill})` : "none",
                        fontFamily: FONT, fontSize: F_CHIP, fontWeight: 600, whiteSpace: "nowrap",
                        position: "relative", overflow: "visible",
                      }}>
                        <div style={{ transform: `scale(${starScale})`, transformOrigin: "center" }}>
                          <StarIcon
                            size={19}
                            fill={fill > 0.5 ? "#ffffff" : "none"}
                            stroke={unlock > 0.5 ? "#ffffff" : DEAD}
                            strokeW={fill > 0.5 ? 0 : 1.8}
                          />
                        </div>
                        <div style={{ position: "relative", height: 24, minWidth: 96 }}>
                          <span style={{
                            position: "absolute", left: 0, top: 0, opacity: 1 - labelSwap,
                            color: unlock > 0.5 ? "#fff" : DEAD,
                          }}>Give Hype</span>
                          <span style={{ position: "absolute", left: 0, top: 0, opacity: labelSwap, color: "#fff" }}>
                            Hyped ✦
                          </span>
                        </div>
                        <Ripple t={t} at={HYPE_TAP} x={26} y={18} />
                      </div>
                      {/* +1 floater */}
                      <div style={{
                        position: "absolute", left: "50%", top: -6,
                        transform: `translate(-50%, ${plusY}px)`, opacity: plusOp,
                        fontFamily: FONT, fontWeight: 800, fontSize: 20,
                        color: "#fff", textShadow: `0 2px 10px ${PINK}`,
                      }}>+1</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── LEADERBOARD RANK BADGE (after hyping) ── */}
            <div style={{
              marginTop: 16,
              transform: `scale(${badgeSpr}) translateY(${(1 - badgeSpr) * 10}px)`,
              opacity: Math.min(badgeSpr, 1), transformOrigin: "left center",
              display: "inline-flex", alignItems: "center", gap: 9,
              height: 42, padding: "0 16px", borderRadius: 10, background: HYPE_GRAD,
              boxShadow: "0 8px 24px rgba(139,92,255,0.35)",
            }}>
              <StarIcon size={22} fill="#ffffff" />
              <span style={{ color: "#fff", fontFamily: FONT, fontSize: F_BADGE, fontWeight: 700 }}>
                #3 on the Hype leaderboard this week
              </span>
            </div>

            {/* Up-next rows — fill the lower screen so the phone reads as a real feed
                rather than a mock floating in dead space. Purely set dressing.
                Hidden when overlaid on a 16:9 doc: no other videos should be visible. */}
            <div style={{
              marginTop: 20, borderTop: `1px solid ${TH.hairline}`, paddingTop: 16,
              display: showUpNext ? "block" : "none",
            }}>
              {[
                { t: "कार्तिक पूर्णिमा · देव दीपावली की रात", v: "1.4M views" },
                { t: "तुलसी विवाह का रहस्य", v: "880K views" },
              ].map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14 }}>
                  <div style={{
                    width: 148, height: 84, borderRadius: 10, flexShrink: 0,
                    background: i === 0
                      ? "linear-gradient(140deg,#42225e,#1a1226)"
                      : "linear-gradient(140deg,#5e3722,#261a12)",
                  }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      color: TXT, fontFamily: FONT_HI, fontSize: 14, fontWeight: 600,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 400,
                    }}>{r.t}</div>
                    <div style={{ color: SUBTXT, fontFamily: FONT, fontSize: 12, marginTop: 5 }}>
                      Deva Katha &amp; Mandir Yatra · {r.v}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Standalone portrait preview (720×1280) — own backdrop, up-next rows visible.
export const HypeButton: React.FC = () => (
  <AbsoluteFill style={{
    background: "radial-gradient(circle at 50% 35%, #2a1140 0%, #120a1c 55%, #08060c 100%)",
    justifyContent: "center", alignItems: "center",
  }}>
    <PhoneMock />
  </AbsoluteFill>
);

// ─────────────────────────────────────────────────────────────────────────────
// HypePhone16x9 — how it ACTUALLY ships: the phone popping up over the real doc.
// Base = a cut of the finished 720p at the mid-roll CTA (Kedarnath 1f1e80a8,
// segment 110s–126s; the narrator says "सब्सक्राइब" at 115.77s → t=5.77s here).
// The phone rises from the BOTTOM-RIGHT corner, and the Subscribe tap is timed to
// land ON that word. No up-next rows — no other videos visible.
// NOTE: the doc's native frame is 1280×704 @ 16fps, so this comp matches the base
// exactly rather than assuming 1280×720/30.
// ─────────────────────────────────────────────────────────────────────────────
// Word timings from productions.word_timestamps_hindi (Kedarnath 1f1e80a8), rebased to
// this 110s cut. The narrator's CTA line drives the choreography:
//   3.4  "पर आगे बढ़ने से पहले,"
//   4.9  "हमारे चैनल को"
//   5.8  "सब्सक्राइब करें,"          → Subscribe tap
//   8.4  "कमेंट बॉक्स में लिखें,"     → the comment is what's on screen
//  10.4  "जय केदारनाथ। हर हर महादेव।" → Like → unlock → Hype lands on the blessing
const W_SUBSCRIBE = 5.8;
const W_COMMENT = 8.4;
const W_JAI = 10.4;
const POP_S = 4.2;               // phone rises just before "हमारे चैनल को"

const NARRATION_BEATS: Beats = {
  sub: W_SUBSCRIBE,        // taps exactly on "सब्सक्राइब"
  like: W_JAI + 0.3,       // like/hype ride the "जय केदारनाथ" blessing…
  unlock: W_JAI + 0.8,     // …so nothing fires while he's talking about something else
  rotate: W_JAI + 1.1,
  hype: W_JAI + 2.1,
  badge: W_JAI + 3.2,
};

export const HypePhone16x9: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, height } = useVideoConfig();
  const t = frame / fps;

  // rise from below the frame, settle, then drop back out at the tail
  const rise = spring({
    frame: frame - Math.round(POP_S * fps), fps,
    config: { damping: 16, stiffness: 90 }, durationInFrames: Math.round(0.9 * fps),
  });
  const OUT_S = NARRATION_BEATS.badge + 1.3;   // leave after the badge has registered
  const fall = spring({
    frame: frame - Math.round(OUT_S * fps), fps,
    config: { damping: 18, stiffness: 90 }, durationInFrames: Math.round(0.8 * fps),
  });
  const y = interpolate(rise, [0, 1], [height, 0]) + interpolate(fall, [0, 1], [0, height]);

  // Keep the phone MODEST — it was eating the frame. The inner video shrinks to a token
  // strip (it only exists to say "this is a phone"), which pulls the comment box — the
  // actual subject — up into the focus. Anchor so content bottom == frame bottom so the
  // dead tail runs off-screen.
  // The player MUST stay 16:9 (= 358 against the 636 screen) — shrinking it cropped the
  // video. To keep the phone modest we scale the WHOLE phone down instead.
  const SCALE = 0.58;
  const CONTENT_H = 716;                      // 806 − the 90px the shrunk player gave up
  const top = 704 - CONTENT_H * SCALE - 6;    // content bottom ≈ frame bottom

  return (
    <AbsoluteFill>
      <OffthreadVideo src={staticFile("kedar_cta_seg.mp4")} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      <AbsoluteFill style={{ overflow: "hidden" }}>
        <div style={{
          position: "absolute", right: 34, top,
          transform: `translateY(${y}px) scale(${SCALE})`, transformOrigin: "top right",
        }}>
          <PhoneMock beats={NARRATION_BEATS} showUpNext={false} fadeOut={false}
                     screenSrc={staticFile("kedar_cta_seg.mp4")}
                     title="केदारनाथ धाम · स्थापना और पंच केदार" />
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
